import React, { useState, useEffect } from "react";
import "@/App.css";
import "@/index.css";
import "./lib/auth"; // axios interceptors + token storage
import { authStorage } from "./lib/auth";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { 
  BookOpen, 
  Calculator, 
  ChartBar, 
  ListChecks, 
  PencilLine,
  Trophy,
  Clock,
  CaretRight,
  CheckCircle,
  XCircle,
  Lightbulb,
  GraduationCap,
  Scales,
  X,
  Backspace,
  Percent,
  PlusMinus
} from "@phosphor-icons/react";
import { AuthScreen, UserMenu } from "./components/Auth";
import { AITutor, TutorFab } from "./components/AITutor";
import { UpgradeModal, UpgradeButton, usePaymentPolling } from "./components/Upgrade";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configure axios for auth
axios.defaults.withCredentials = true;

// Floating Calculator Component
function FloatingCalculator() {
  const [isOpen, setIsOpen] = useState(false);
  const [display, setDisplay] = useState('0');
  const [previousValue, setPreviousValue] = useState(null);
  const [operator, setOperator] = useState(null);
  const [waitingForOperand, setWaitingForOperand] = useState(false);
  const [memory, setMemory] = useState(0);
  const dragRef = React.useRef(null);

  const inputDigit = (digit) => {
    if (waitingForOperand) {
      setDisplay(digit);
      setWaitingForOperand(false);
    } else {
      setDisplay(display === '0' ? digit : display + digit);
    }
  };

  const inputDecimal = () => {
    if (waitingForOperand) {
      setDisplay('0.');
      setWaitingForOperand(false);
      return;
    }
    if (!display.includes('.')) {
      setDisplay(display + '.');
    }
  };

  const clear = () => {
    setDisplay('0');
    setPreviousValue(null);
    setOperator(null);
    setWaitingForOperand(false);
  };

  const clearEntry = () => {
    setDisplay('0');
  };

  const toggleSign = () => {
    setDisplay(String(-parseFloat(display)));
  };

  const inputPercent = () => {
    const value = parseFloat(display);
    setDisplay(String(value / 100));
  };

  const performOperation = (nextOperator) => {
    const inputValue = parseFloat(display);

    if (previousValue === null) {
      setPreviousValue(inputValue);
    } else if (operator) {
      const currentValue = previousValue || 0;
      let result;
      
      switch (operator) {
        case '+':
          result = currentValue + inputValue;
          break;
        case '-':
          result = currentValue - inputValue;
          break;
        case '*':
          result = currentValue * inputValue;
          break;
        case '/':
          result = inputValue !== 0 ? currentValue / inputValue : 'Error';
          break;
        default:
          result = inputValue;
      }
      
      setDisplay(String(result));
      setPreviousValue(result);
    }

    setWaitingForOperand(true);
    setOperator(nextOperator);
  };

  const calculate = () => {
    if (!operator || previousValue === null) return;
    
    const inputValue = parseFloat(display);
    let result;
    
    switch (operator) {
      case '+':
        result = previousValue + inputValue;
        break;
      case '-':
        result = previousValue - inputValue;
        break;
      case '*':
        result = previousValue * inputValue;
        break;
      case '/':
        result = inputValue !== 0 ? previousValue / inputValue : 'Error';
        break;
      default:
        result = inputValue;
    }
    
    setDisplay(String(result));
    setPreviousValue(null);
    setOperator(null);
    setWaitingForOperand(true);
  };

  const memoryAdd = () => {
    setMemory(memory + parseFloat(display));
  };

  const memoryRecall = () => {
    setDisplay(String(memory));
    setWaitingForOperand(true);
  };

  const memoryClear = () => {
    setMemory(0);
  };

  const CalcButton = ({ onClick, children, className = '', wide = false }) => (
    <button
      onClick={onClick}
      className={`${wide ? 'col-span-2' : ''} h-12 rounded font-mono text-lg font-semibold transition-all active:scale-95 ${className}`}
    >
      {children}
    </button>
  );

  return (
    <>
      {/* Drag constraints boundary — full viewport */}
      <div ref={dragRef} className="fixed inset-0 pointer-events-none z-[9998]" aria-hidden="true" />

      {/* Draggable Calculator (button + panel move together) */}
      <motion.div
        drag
        dragMomentum={false}
        dragElastic={0}
        dragConstraints={dragRef}
        className="fixed bottom-20 right-6 z-[9999]"
        data-testid="calculator-draggable"
      >
        <motion.button
          data-testid="calculator-toggle"
          onClick={() => setIsOpen(!isOpen)}
          className="w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-colors hover:brightness-110 cursor-grab active:cursor-grabbing"
          style={{ 
            backgroundColor: isOpen ? 'var(--error)' : 'var(--primary)',
            color: 'white'
          }}
          whileTap={{ scale: 0.95 }}
        >
          {isOpen ? <X size={24} weight="bold" /> : <Calculator size={28} weight="fill" />}
        </motion.button>

        {/* Calculator Panel — absolute relative to the draggable container */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, y: 20, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.9 }}
              className="absolute bottom-16 right-0 w-72 rounded-lg shadow-2xl overflow-hidden"
              style={{ backgroundColor: 'var(--surface)', border: '1px solid var(--border)' }}
            >
            {/* Header */}
            <div className="px-4 py-2 flex items-center justify-between cursor-grab active:cursor-grabbing" style={{ backgroundColor: 'var(--primary)', color: 'white' }}>
              <span className="font-display font-semibold">Calculator</span>
              <div className="flex gap-2 text-xs font-mono opacity-80">
                {memory !== 0 && <span>M</span>}
                <span className="opacity-70">⋮⋮</span>
              </div>
            </div>

            {/* Display */}
            <div className="p-4" style={{ backgroundColor: 'var(--surface-secondary)' }}>
              <div 
                className="text-right font-mono text-3xl font-bold truncate"
                style={{ color: 'var(--text-primary)' }}
              >
                {display.length > 12 ? parseFloat(display).toExponential(6) : 
                  isNaN(parseFloat(display)) ? display : 
                  parseFloat(display).toLocaleString('en-US', { maximumFractionDigits: 10 })}
              </div>
              {operator && previousValue !== null && (
                <div className="text-right font-mono text-sm opacity-50">
                  {previousValue} {operator}
                </div>
              )}
            </div>

            {/* Memory Row */}
            <div className="grid grid-cols-4 gap-1 px-2 py-1" style={{ backgroundColor: 'var(--surface-secondary)' }}>
              <CalcButton onClick={memoryClear} className="text-xs bg-transparent hover:bg-[#E6E1D6]">MC</CalcButton>
              <CalcButton onClick={memoryRecall} className="text-xs bg-transparent hover:bg-[#E6E1D6]">MR</CalcButton>
              <CalcButton onClick={memoryAdd} className="text-xs bg-transparent hover:bg-[#E6E1D6]">M+</CalcButton>
              <CalcButton onClick={clearEntry} className="text-xs bg-transparent hover:bg-[#E6E1D6]">CE</CalcButton>
            </div>

            {/* Buttons */}
            <div className="grid grid-cols-4 gap-1 p-2">
              <CalcButton onClick={clear} className="bg-[#8B0000]/10 text-[#8B0000] hover:bg-[#8B0000]/20">C</CalcButton>
              <CalcButton onClick={toggleSign} className="bg-[#E6E1D6] hover:bg-[#d4cfc5]"><PlusMinus size={18} /></CalcButton>
              <CalcButton onClick={inputPercent} className="bg-[#E6E1D6] hover:bg-[#d4cfc5]"><Percent size={18} /></CalcButton>
              <CalcButton onClick={() => performOperation('/')} className="bg-[#163300] text-white hover:bg-[#2B4D16]">÷</CalcButton>

              <CalcButton onClick={() => inputDigit('7')} className="bg-white hover:bg-[#F0EBE1] border border-[#E6E1D6]">7</CalcButton>
              <CalcButton onClick={() => inputDigit('8')} className="bg-white hover:bg-[#F0EBE1] border border-[#E6E1D6]">8</CalcButton>
              <CalcButton onClick={() => inputDigit('9')} className="bg-white hover:bg-[#F0EBE1] border border-[#E6E1D6]">9</CalcButton>
              <CalcButton onClick={() => performOperation('*')} className="bg-[#163300] text-white hover:bg-[#2B4D16]">×</CalcButton>

              <CalcButton onClick={() => inputDigit('4')} className="bg-white hover:bg-[#F0EBE1] border border-[#E6E1D6]">4</CalcButton>
              <CalcButton onClick={() => inputDigit('5')} className="bg-white hover:bg-[#F0EBE1] border border-[#E6E1D6]">5</CalcButton>
              <CalcButton onClick={() => inputDigit('6')} className="bg-white hover:bg-[#F0EBE1] border border-[#E6E1D6]">6</CalcButton>
              <CalcButton onClick={() => performOperation('-')} className="bg-[#163300] text-white hover:bg-[#2B4D16]">−</CalcButton>

              <CalcButton onClick={() => inputDigit('1')} className="bg-white hover:bg-[#F0EBE1] border border-[#E6E1D6]">1</CalcButton>
              <CalcButton onClick={() => inputDigit('2')} className="bg-white hover:bg-[#F0EBE1] border border-[#E6E1D6]">2</CalcButton>
              <CalcButton onClick={() => inputDigit('3')} className="bg-white hover:bg-[#F0EBE1] border border-[#E6E1D6]">3</CalcButton>
              <CalcButton onClick={() => performOperation('+')} className="bg-[#163300] text-white hover:bg-[#2B4D16]">+</CalcButton>

              <CalcButton onClick={() => inputDigit('0')} wide className="bg-white hover:bg-[#F0EBE1] border border-[#E6E1D6]">0</CalcButton>
              <CalcButton onClick={inputDecimal} className="bg-white hover:bg-[#F0EBE1] border border-[#E6E1D6]">.</CalcButton>
              <CalcButton onClick={calculate} className="bg-[#2D5A27] text-white hover:bg-[#3d7a37]">=</CalcButton>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      </motion.div>
    </>
  );
}

// Practice mode icons and colors
const PRACTICE_MODES = {
  spot_errors: { 
    icon: ListChecks, 
    label: "Find the Error", 
    desc: "Identify mistakes in financial statements",
    color: "#8B0000"
  },
  calculate: { 
    icon: Calculator, 
    label: "Calculate Values", 
    desc: "Compute missing figures",
    color: "#B8860B"
  },
  classify: { 
    icon: BookOpen, 
    label: "Classify Transactions", 
    desc: "Categorize accounts correctly",
    color: "#2D5A27"
  },
  analyze: { 
    icon: ChartBar, 
    label: "Analyze & Interpret", 
    desc: "Calculate ratios and insights",
    color: "#163300"
  },
  build: { 
    icon: PencilLine, 
    label: "Build from Scenario", 
    desc: "Create statements from transactions",
    color: "#8C7D64"
  }
};

const STATEMENT_TYPES = {
  income_statement: { label: "Income Statement", icon: "📊" },
  balance_sheet: { label: "Balance Sheet", icon: "⚖️" },
  cash_flow: { label: "Cash Flow Statement", icon: "💰" },
  journal_entries: { label: "Journal Entries", icon: "📒" }
};

const DIFFICULTY_LEVELS = ["beginner", "intermediate", "advanced"];

const MASTERY_COLORS = {
  "not_started": "#9CA3AF",
  "learning": "#EF4444",
  "practicing": "#F59E0B", 
  "proficient": "#3B82F6",
  "mastered": "#10B981"
};

const MASTERY_LABELS = {
  "not_started": "Not Started",
  "learning": "Learning",
  "practicing": "Practicing",
  "proficient": "Proficient",
  "mastered": "Mastered"
};

// Dashboard Component
function Dashboard({ onStartQuiz, progress, hikariStatus, learningProfile, user, onLogout, subscription, onOpenUpgrade, onOpenTutor }) {
  const [selectedChapter, setSelectedChapter] = useState(null);
  const [selectedMode, setSelectedMode] = useState("spot_errors");
  const [selectedStatement, setSelectedStatement] = useState("income_statement");
  const [selectedStandard, setSelectedStandard] = useState("GAAP");
  const [selectedDifficulty, setSelectedDifficulty] = useState("beginner");
  const [chapters, setChapters] = useState([]);
  const [bankStats, setBankStats] = useState(null);

  useEffect(() => {
    axios.get(`${API}/chapters`).then(res => setChapters(res.data.chapters)).catch(console.error);
    axios.get(`${API}/question-bank/stats`).then(res => setBankStats(res.data)).catch(console.error);
  }, []);

  // Set recommended chapter as default when profile loads
  useEffect(() => {
    if (learningProfile?.recommended_chapter && selectedChapter === null) {
      setSelectedChapter(learningProfile.recommended_chapter);
    } else if (selectedChapter === null) {
      setSelectedChapter(1);
    }
  }, [learningProfile, selectedChapter]);

  const handleStartQuiz = () => {
    onStartQuiz({
      chapter: selectedChapter || 1,
      practice_mode: selectedMode,
      statement_type: selectedStatement,
      standard: selectedStandard,
      difficulty: selectedDifficulty
    });
  };

  const getChapterMastery = (chapterNum) => {
    return learningProfile?.chapter_mastery?.[String(chapterNum)] || { mastery_level: "not_started" };
  };

  return (
    <div className="min-h-screen p-6 lg:p-8" style={{ backgroundColor: 'var(--background)' }}>
      {/* Header */}
      <header className="max-w-7xl mx-auto mb-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4 mb-2">
            <GraduationCap size={48} weight="fill" style={{ color: 'var(--primary)' }} />
            <div>
              <h1 className="font-display text-4xl lg:text-5xl font-black tracking-tighter" style={{ color: 'var(--text-primary)' }}>
                FMAA Apprentice Mode
              </h1>
              <p className="font-mono text-sm" style={{ color: 'var(--text-secondary)' }}>
                Financial & Managerial Accounting · Master-Guided Practice
              </p>
            </div>
          </div>
          {/* User Menu & Status */}
          <div className="flex items-center gap-3">
            {bankStats && (
              <div 
                className="hidden lg:flex px-3 py-2 rounded text-xs font-mono items-center gap-2"
                style={{ backgroundColor: 'var(--surface-secondary)' }}
                data-testid="bank-stats"
              >
                <span className="text-[#2D5A27]">⚡</span>
                <span>{bankStats.total_quizzes} cached</span>
              </div>
            )}
            <UpgradeButton isPro={subscription?.is_pro} onClick={onOpenUpgrade} />
            {user && <UserMenu user={user} onLogout={onLogout} />}
          </div>
        </div>
      </header>

      {/* Coaching Recommendation Banner */}
      {learningProfile?.recommended_focus && (
        <motion.div 
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-7xl mx-auto mb-6"
        >
          <div className="card p-4 flex items-center gap-4" style={{ borderLeft: '4px solid var(--primary)' }}>
            <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'var(--primary)', color: 'white' }}>
              <GraduationCap size={24} weight="fill" />
            </div>
            <div className="flex-1">
              <div className="font-mono text-xs uppercase tracking-widest opacity-60 mb-1">Your Coach Says</div>
              <div className="font-display text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
                {learningProfile.recommended_focus}
              </div>
            </div>
            {learningProfile?.stats && (
              <div className="hidden md:flex items-center gap-6 text-center">
                <div>
                  <div className="font-display text-2xl font-bold" style={{ color: 'var(--primary)' }}>
                    {learningProfile.stats.chapters_mastered}/20
                  </div>
                  <div className="text-xs opacity-60">Mastered</div>
                </div>
                <div>
                  <div className="font-display text-2xl font-bold" style={{ color: 'var(--success)' }}>
                    {learningProfile.stats.overall_readiness}%
                  </div>
                  <div className="text-xs opacity-60">Ready</div>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      )}

      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Left Column - Chapter Selection */}
        <div className="lg:col-span-1">
          <div className="card p-4 h-full">
            <h3 className="font-display text-lg font-semibold mb-4 flex items-center gap-2">
              <BookOpen size={20} weight="fill" />
              Select Chapter
            </h3>
            <div className="space-y-1 max-h-[400px] overflow-y-auto pr-2">
              {chapters.map(ch => {
                const mastery = getChapterMastery(ch.num);
                const masteryLevel = mastery.mastery_level || "not_started";
                const isRecommended = learningProfile?.recommended_chapter === ch.num;
                
                return (
                  <button
                    key={ch.num}
                    data-testid={`chapter-${ch.num}-btn`}
                    onClick={() => setSelectedChapter(ch.num)}
                    className={`w-full text-left p-3 rounded text-sm transition-all relative ${
                      selectedChapter === ch.num 
                        ? 'bg-[#163300] text-white' 
                        : 'hover:bg-[#F0EBE1]'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-xs opacity-60">Ch.{ch.num}</span>
                      <div className="flex items-center gap-1">
                        {isRecommended && (
                          <span className="text-xs px-1.5 py-0.5 rounded bg-[#F59E0B] text-white">Next</span>
                        )}
                        <span 
                          className="w-2 h-2 rounded-full" 
                          style={{ backgroundColor: MASTERY_COLORS[masteryLevel] }}
                          title={MASTERY_LABELS[masteryLevel]}
                        />
                      </div>
                    </div>
                    <div className="font-medium truncate">{ch.title}</div>
                    {mastery.attempts > 0 && (
                      <div className="text-xs opacity-60 mt-1">
                        {mastery.average_score}% avg · {mastery.attempts} attempts
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Middle Column - Practice Mode & Statement Type */}
        <div className="lg:col-span-2 space-y-6">
          {/* Practice Modes */}
          <div className="card p-6">
            <h3 className="font-display text-lg font-semibold mb-4">Practice Mode</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {Object.entries(PRACTICE_MODES).map(([key, mode]) => {
                const Icon = mode.icon;
                return (
                  <button
                    key={key}
                    data-testid={`mode-${key}-btn`}
                    onClick={() => setSelectedMode(key)}
                    className={`p-4 rounded border text-left transition-all ${
                      selectedMode === key 
                        ? 'border-[#163300] bg-[#163300]/5' 
                        : 'border-[#E6E1D6] hover:border-[#8C7D64]'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-10 h-10 rounded flex items-center justify-center"
                        style={{ backgroundColor: `${mode.color}15`, color: mode.color }}
                      >
                        <Icon size={24} weight="fill" />
                      </div>
                      <div>
                        <div className="font-semibold text-sm">{mode.label}</div>
                        <div className="text-xs opacity-60">{mode.desc}</div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Statement Type */}
          <div className="card p-6">
            <h3 className="font-display text-lg font-semibold mb-4">Statement Type</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {Object.entries(STATEMENT_TYPES).map(([key, type]) => (
                <button
                  key={key}
                  data-testid={`statement-${key}-btn`}
                  onClick={() => setSelectedStatement(key)}
                  className={`p-4 rounded border text-center transition-all ${
                    selectedStatement === key 
                      ? 'border-[#163300] bg-[#163300]/5' 
                      : 'border-[#E6E1D6] hover:border-[#8C7D64]'
                  }`}
                >
                  <div className="text-2xl mb-2">{type.icon}</div>
                  <div className="font-mono text-xs">{type.label}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Standards & Difficulty */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="card p-4">
              <h4 className="font-mono text-xs uppercase tracking-widest mb-3 opacity-60">Accounting Standard</h4>
              <div className="standard-toggle">
                <button 
                  data-testid="standard-gaap-btn"
                  className={selectedStandard === 'GAAP' ? 'active' : ''} 
                  onClick={() => setSelectedStandard('GAAP')}
                >
                  GAAP
                </button>
                <button 
                  data-testid="standard-ifrs-btn"
                  className={selectedStandard === 'IFRS' ? 'active' : ''} 
                  onClick={() => setSelectedStandard('IFRS')}
                >
                  IFRS
                </button>
              </div>
            </div>
            <div className="card p-4">
              <h4 className="font-mono text-xs uppercase tracking-widest mb-3 opacity-60">Difficulty</h4>
              <div className="flex gap-2">
                {DIFFICULTY_LEVELS.map(level => (
                  <button
                    key={level}
                    data-testid={`difficulty-${level}-btn`}
                    onClick={() => setSelectedDifficulty(level)}
                    className={`flex-1 py-2 px-3 rounded text-xs font-mono uppercase tracking-wider transition-all ${
                      selectedDifficulty === level 
                        ? 'bg-[#163300] text-white' 
                        : 'bg-[#F0EBE1] hover:bg-[#E6E1D6]'
                    }`}
                  >
                    {level}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Progress & Start */}
        <div className="lg:col-span-1 space-y-6">
          {/* Progress Card */}
          <div className="card p-4">
            <h3 className="font-display text-lg font-semibold mb-4 flex items-center gap-2">
              <Trophy size={20} weight="fill" style={{ color: '#B8860B' }} />
              Your Progress
            </h3>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="opacity-60">Quizzes Completed</span>
                  <span className="font-bold">{progress?.total_quizzes || 0}</span>
                </div>
                <div className="progress-bar">
                  <div className="progress-bar-fill" style={{ width: `${Math.min((progress?.total_quizzes || 0) * 5, 100)}%` }} />
                </div>
              </div>
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="opacity-60">Total Correct</span>
                  <span className="font-bold">{progress?.total_correct || 0}</span>
                </div>
              </div>
              <div className="pt-2 border-t" style={{ borderColor: 'var(--border)' }}>
                <div className="flex items-center gap-2 text-xs opacity-60">
                  <Clock size={14} />
                  Last active: {progress?.last_active ? new Date(progress.last_active).toLocaleDateString() : 'Never'}
                </div>
              </div>
            </div>
          </div>

          {/* Selected Summary */}
          <div className="card p-4" style={{ backgroundColor: 'var(--surface-secondary)' }}>
            <h4 className="font-mono text-xs uppercase tracking-widest mb-3 opacity-60">Quiz Configuration</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="opacity-60">Chapter</span>
                <span className="font-semibold">{selectedChapter}</span>
              </div>
              <div className="flex justify-between">
                <span className="opacity-60">Mode</span>
                <span className="font-semibold">{PRACTICE_MODES[selectedMode].label}</span>
              </div>
              <div className="flex justify-between">
                <span className="opacity-60">Type</span>
                <span className="font-semibold">{STATEMENT_TYPES[selectedStatement].label}</span>
              </div>
              <div className="flex justify-between">
                <span className="opacity-60">Standard</span>
                <span className="font-semibold">{selectedStandard}</span>
              </div>
            </div>
          </div>

          {/* Start Button */}
          <button 
            data-testid="start-quiz-btn"
            onClick={handleStartQuiz}
            className="btn-primary w-full text-center flex items-center justify-center gap-2 py-4"
          >
            <span className="font-display text-lg">Begin Practice</span>
            <CaretRight size={20} weight="bold" />
          </button>
        </div>
      </div>
    </div>
  );
}

// Financial Statement Display Component
function FinancialStatement({ data, companyName, fiscalPeriod, statementType }) {
  if (!data || !data.sections) return null;

  const formatValue = (value) => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string' && value.includes('?')) return <span className="missing-value">?</span>;
    const num = parseFloat(value);
    if (isNaN(num)) return value;
    return num.toLocaleString('en-US', { 
      style: 'currency', 
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
  };

  return (
    <div className="card p-6" data-testid="financial-statement">
      <div className="text-center mb-6 pb-4 border-b" style={{ borderColor: 'var(--border)' }}>
        <h2 className="font-display text-2xl font-bold">{companyName}</h2>
        <h3 className="font-display text-lg" style={{ color: 'var(--text-secondary)' }}>
          {STATEMENT_TYPES[statementType]?.label || 'Financial Statement'}
        </h3>
        <p className="font-mono text-sm opacity-60">{fiscalPeriod}</p>
      </div>
      
      <table className="financial-table">
        <tbody>
          {data.sections.map((section, sIdx) => (
            <React.Fragment key={sIdx}>
              {section.title && (
                <tr>
                  <td colSpan="2" className="font-bold pt-4 pb-2" style={{ color: 'var(--primary)' }}>
                    {section.title}
                  </td>
                </tr>
              )}
              {section.items.map((item, iIdx) => (
                <tr 
                  key={iIdx} 
                  className={`
                    ${item.is_total ? 'total-row' : ''}
                    ${item.is_grand_total ? 'grand-total-row' : ''}
                  `}
                >
                  <td className={`indent-${item.indent || 0}`}>
                    {item.label}
                  </td>
                  <td className="value-cell">
                    {item.is_blank ? '' : formatValue(item.value)}
                  </td>
                </tr>
              ))}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Question Display Component
function QuestionPanel({ question, index, total, onAnswer, userAnswer, evaluation, isSubmitting }) {
  const [answer, setAnswer] = useState(userAnswer || '');

  useEffect(() => {
    setAnswer(userAnswer || '');
  }, [userAnswer, question]);

  const handleSubmit = () => {
    if (answer.trim()) {
      onAnswer(answer);
    }
  };

  const isAnswered = evaluation !== null && evaluation !== undefined;

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="card p-6"
      data-testid={`question-${index}`}
    >
      <div className="flex items-center justify-between mb-4">
        <span className="font-mono text-xs uppercase tracking-widest opacity-60">
          Question {index + 1} of {total}
        </span>
        <span className="font-mono text-sm font-bold" style={{ color: 'var(--primary)' }}>
          {question.points} pts
        </span>
      </div>

      <h4 className="font-display text-xl font-semibold mb-4">{question.question}</h4>

      {question.type === 'multiple_choice' && question.options && (
        <div className="space-y-2 mb-4">
          {question.options.map((option, idx) => (
            <button
              key={idx}
              data-testid={`option-${idx}`}
              onClick={() => !isAnswered && setAnswer(option)}
              disabled={isAnswered}
              className={`w-full text-left p-3 rounded border transition-all ${
                answer === option 
                  ? isAnswered 
                    ? evaluation?.is_correct 
                      ? 'border-[#2D5A27] bg-[#2D5A27]/10' 
                      : 'border-[#8B0000] bg-[#8B0000]/10'
                    : 'border-[#163300] bg-[#163300]/5'
                  : 'border-[#E6E1D6] hover:border-[#8C7D64]'
              } ${isAnswered ? 'cursor-default' : 'cursor-pointer'}`}
            >
              <span className="font-mono">{option}</span>
            </button>
          ))}
        </div>
      )}

      {(question.type === 'fill_blank' || question.type === 'short_answer' || question.type === 'identify') && (
        <div className="mb-4">
          <input
            type="text"
            data-testid="answer-input"
            value={answer}
            onChange={(e) => !isAnswered && setAnswer(e.target.value)}
            disabled={isAnswered}
            placeholder="Type your answer..."
            className="w-full p-3 border rounded font-mono focus:outline-none focus:ring-2 focus:ring-[#163300]"
            style={{ borderColor: 'var(--border)' }}
          />
        </div>
      )}

      {/* Hint box with formula */}
      {(question.hint || question.formula) && !isAnswered && (
        <div className="mb-4 rounded overflow-hidden" style={{ border: '1px solid var(--border)' }}>
          {/* Formula highlight */}
          {question.formula && (
            <div className="px-4 py-2 font-mono text-sm flex items-center gap-2" style={{ backgroundColor: 'var(--primary)', color: 'white' }}>
              <Calculator size={16} weight="bold" />
              <span>{question.formula}</span>
            </div>
          )}
          {/* Hint text */}
          {question.hint && (
            <div className="flex items-start gap-2 p-3" style={{ backgroundColor: 'var(--surface-secondary)' }}>
              <Lightbulb size={18} style={{ color: 'var(--warning)' }} className="mt-0.5 flex-shrink-0" />
              <span className="text-sm opacity-80">{question.hint}</span>
            </div>
          )}
        </div>
      )}

      {!isAnswered ? (
        <button
          data-testid="submit-answer-btn"
          onClick={handleSubmit}
          disabled={!answer.trim() || isSubmitting}
          className="btn-primary w-full flex items-center justify-center gap-2"
        >
          {isSubmitting ? 'Evaluating...' : 'Submit Answer'}
        </button>
      ) : (
        <AnimatePresence>
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="mt-4"
          >
            <div className={`flex items-center gap-2 mb-3 ${evaluation?.is_correct ? 'text-[#2D5A27]' : 'text-[#8B0000]'}`}>
              {evaluation?.is_correct ? (
                <CheckCircle size={24} weight="fill" />
              ) : (
                <XCircle size={24} weight="fill" />
              )}
              <span className="font-display text-lg font-semibold">
                {evaluation?.is_correct ? 'Correct!' : 'Not Quite'}
              </span>
              <span className="ml-auto font-mono">+{evaluation?.score || 0} pts</span>
            </div>
            <div className="masters-panel">
              {/* Formula highlight box */}
              {evaluation?.formula_used && (
                <div className="mb-3 ml-6 p-3 rounded font-mono text-sm" style={{ backgroundColor: 'var(--surface-secondary)', borderLeft: '3px solid var(--primary)' }}>
                  <span className="text-xs uppercase tracking-widest opacity-60 block mb-1">Formula</span>
                  <span style={{ color: 'var(--primary)' }}>{evaluation.formula_used}</span>
                </div>
              )}
              
              {/* Feedback with formatted math */}
              <div className="pl-6 text-sm whitespace-pre-line" style={{ color: 'var(--text-secondary)' }}>
                {(evaluation?.feedback || "The Master is reviewing your answer...").split('\n').map((line, i) => {
                  // Highlight calculation lines (lines with = or numbers)
                  const isCalculation = /^[\s]*[\$\d].*=.*[\$\d]|^.*:\s*\$[\d,]+/.test(line);
                  const isFormula = /^Formula:|^Step \d|^Calculation:/i.test(line);
                  
                  if (isCalculation || isFormula) {
                    return (
                      <div key={i} className="font-mono my-1 p-2 rounded" style={{ backgroundColor: 'var(--surface-secondary)' }}>
                        {line}
                      </div>
                    );
                  }
                  return <p key={i} className="my-1">{line}</p>;
                })}
              </div>
              
              {!evaluation?.is_correct && evaluation?.correct_answer_explanation && (
                <div className="mt-4 pl-6 p-3 rounded" style={{ backgroundColor: '#8B0000/10', borderLeft: '3px solid var(--error)' }}>
                  <span className="text-xs uppercase tracking-widest opacity-60 block mb-2">Correct Solution</span>
                  <div className="text-sm whitespace-pre-line">
                    {evaluation.correct_answer_explanation.split('\n').map((line, i) => {
                      const isCalculation = /^[\s]*[\$\d].*=.*[\$\d]|^.*:\s*\$[\d,]+/.test(line);
                      if (isCalculation) {
                        return (
                          <div key={i} className="font-mono my-1 p-2 rounded" style={{ backgroundColor: 'var(--surface-secondary)' }}>
                            {line}
                          </div>
                        );
                      }
                      return <p key={i} className="my-1">{line}</p>;
                    })}
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </AnimatePresence>
      )}
    </motion.div>
  );
}

// Quiz Screen Component
function QuizScreen({ quizConfig, onBack }) {
  const [quiz, setQuiz] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [evaluations, setEvaluations] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [results, setResults] = useState(null);

  useEffect(() => {
    generateQuiz();
  }, []);

  const generateQuiz = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.post(`${API}/quiz/generate`, quizConfig, {
        timeout: 120000 // 120 second timeout for Claude AI generation
      });
      setQuiz(response.data);
    } catch (err) {
      console.error('Quiz generation error:', err);
      setError(err.response?.data?.detail || 'Failed to generate quiz. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswer = async (answer) => {
    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/quiz/${quiz.id}/submit`, {
        quiz_id: quiz.id,
        user_answer: answer,
        question_index: currentQuestion
      }, {
        timeout: 60000 // 60 second timeout for Claude evaluation
      });
      
      setAnswers(prev => ({ ...prev, [currentQuestion]: answer }));
      setEvaluations(prev => ({ ...prev, [currentQuestion]: response.data }));
    } catch (err) {
      console.error('Answer submission error:', err);
      // Set a fallback evaluation so UI doesn't get stuck
      setAnswers(prev => ({ ...prev, [currentQuestion]: answer }));
      setEvaluations(prev => ({ 
        ...prev, 
        [currentQuestion]: {
          is_correct: false,
          score: 0,
          feedback: "The Master couldn't review your answer right now. Please try again or continue to the next question.",
          correct_answer_explanation: ""
        }
      }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNext = () => {
    if (currentQuestion < quiz.questions.length - 1) {
      setCurrentQuestion(prev => prev + 1);
    }
  };

  const handleComplete = async () => {
    try {
      const response = await axios.post(`${API}/quiz/${quiz.id}/complete`);
      setResults(response.data);
      setCompleted(true);
      
      // Update progress
      await axios.post(`${API}/progress/update`, {
        chapter: quizConfig.chapter,
        practice_mode: quizConfig.practice_mode,
        score: response.data.correct_count,
        total: response.data.total_questions
      });
    } catch (err) {
      console.error('Complete quiz error:', err);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--background)' }}>
        <div className="text-center">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
            className="w-16 h-16 border-4 border-t-[#163300] border-r-transparent border-b-transparent border-l-transparent rounded-full mx-auto mb-4"
          />
          <h2 className="font-display text-2xl font-bold mb-2">The Master is preparing your lesson...</h2>
          <p className="font-mono text-sm opacity-60">Checking question bank or generating new content</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ backgroundColor: 'var(--background)' }}>
        <div className="card p-8 max-w-md text-center">
          <XCircle size={48} weight="fill" className="mx-auto mb-4" style={{ color: 'var(--error)' }} />
          <h2 className="font-display text-2xl font-bold mb-2">Something went wrong</h2>
          <p className="text-sm opacity-60 mb-6">{error}</p>
          <div className="flex gap-3 justify-center">
            <button onClick={onBack} className="px-4 py-2 border rounded" style={{ borderColor: 'var(--border)' }}>
              Go Back
            </button>
            <button onClick={generateQuiz} className="btn-primary">
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (completed && results) {
    const pct = results.percentage || 0;
    const tier =
      pct >= 90 ? { label: 'Mastered', color: 'var(--success)', tone: 'Outstanding work!', emoji: '🏆' } :
      pct >= 70 ? { label: 'Proficient', color: 'var(--success)', tone: 'Solid performance — keep climbing.', emoji: '🎯' } :
      pct >= 50 ? { label: 'Getting there', color: '#D68910', tone: 'Decent effort. Review the misses and run it back.', emoji: '💪' } :
                  { label: 'Keep practicing', color: 'var(--error)', tone: 'Every mistake is a map. Let\'s review what tripped you up.', emoji: '📚' };

    const recommendedCh = results.coaching?.recommended_chapter;
    const nextRec = results.coaching?.next_recommendation || 'Pick another chapter from the dashboard to continue.';

    return (
      <div className="min-h-screen p-6 lg:p-8" style={{ backgroundColor: 'var(--background)' }}>
        <div className="max-w-3xl mx-auto space-y-6">
          {/* Hero */}
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="card p-8 text-center"
            data-testid="quiz-complete-hero"
          >
            <motion.div
              initial={{ rotate: -10, scale: 0 }}
              animate={{ rotate: 0, scale: 1 }}
              transition={{ type: 'spring', stiffness: 200, delay: 0.1 }}
              className="text-6xl mb-3"
            >
              {tier.emoji}
            </motion.div>
            <h2 className="font-display text-4xl font-black mb-2" style={{ color: 'var(--text-primary)' }}>
              Quiz Complete!
            </h2>
            <p className="font-display text-lg mb-6" style={{ color: tier.color }}>
              {tier.tone}
            </p>

            <div className="flex items-center justify-center gap-6 mb-2">
              <div>
                <div className="font-display text-6xl font-black" style={{ color: tier.color }}>
                  {results.correct_count}<span className="opacity-40">/{results.total_questions}</span>
                </div>
                <div className="font-mono text-xs uppercase tracking-widest opacity-60 mt-1">Correct</div>
              </div>
              <div className="h-16 w-px" style={{ backgroundColor: 'var(--border)' }} />
              <div>
                <div className="font-display text-6xl font-black" style={{ color: tier.color }}>
                  {pct}%
                </div>
                <div className="font-mono text-xs uppercase tracking-widest opacity-60 mt-1">Score</div>
              </div>
              {results.coaching?.streak > 0 && (
                <>
                  <div className="h-16 w-px" style={{ backgroundColor: 'var(--border)' }} />
                  <div>
                    <div className="font-display text-6xl font-black">
                      🔥<span style={{ color: 'var(--primary)' }}>{results.coaching.streak}</span>
                    </div>
                    <div className="font-mono text-xs uppercase tracking-widest opacity-60 mt-1">Streak</div>
                  </div>
                </>
              )}
            </div>
          </motion.div>

          {/* Coach's take */}
          {results.coaching && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="card p-5"
              style={{ borderLeft: '4px solid var(--primary)' }}
              data-testid="quiz-complete-coach"
            >
              <div className="flex items-center gap-2 mb-2">
                <GraduationCap size={20} weight="fill" style={{ color: 'var(--primary)' }} />
                <span className="font-mono text-xs uppercase tracking-widest opacity-60">Your Coach Says</span>
                <span
                  className="ml-auto px-2 py-1 rounded text-xs font-mono text-white"
                  style={{ backgroundColor: MASTERY_COLORS[results.coaching.mastery_level] || '#9CA3AF' }}
                >
                  {MASTERY_LABELS[results.coaching.mastery_level] || 'Learning'}
                </span>
              </div>
              <p className="font-display text-lg mb-2">{results.coaching.message}</p>
              <div className="text-sm opacity-80">
                <strong>Do next:</strong> {nextRec}
              </div>
            </motion.div>
          )}

          {/* Per-question review */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="card p-6"
            data-testid="quiz-complete-review"
          >
            <h3 className="font-display text-xl font-bold mb-4 flex items-center gap-2">
              <Lightbulb size={22} weight="fill" style={{ color: '#D68910' }} />
              Review Your Answers
            </h3>
            <div className="space-y-4">
              {quiz.questions.map((q, idx) => {
                const evalData = evaluations[idx] || {};
                const userAns = answers[idx];
                const isCorrect = evalData.is_correct;
                return (
                  <div
                    key={idx}
                    className="p-4 rounded border"
                    style={{
                      borderColor: isCorrect ? 'var(--success)' : 'var(--error)',
                      backgroundColor: isCorrect ? 'rgba(45,90,39,0.04)' : 'rgba(139,0,0,0.04)'
                    }}
                    data-testid={`review-q-${idx}`}
                  >
                    <div className="flex items-start gap-3 mb-2">
                      {isCorrect ? (
                        <CheckCircle size={22} weight="fill" style={{ color: 'var(--success)', flexShrink: 0, marginTop: 2 }} />
                      ) : (
                        <XCircle size={22} weight="fill" style={{ color: 'var(--error)', flexShrink: 0, marginTop: 2 }} />
                      )}
                      <div className="flex-1">
                        <div className="font-mono text-xs uppercase tracking-widest opacity-60 mb-1">
                          Question {idx + 1} · {isCorrect ? 'Correct' : 'Incorrect'}
                        </div>
                        <div className="font-display font-semibold" style={{ color: 'var(--text-primary)' }}>
                          {q.question}
                        </div>
                      </div>
                    </div>

                    <div className="ml-8 space-y-2 text-sm">
                      <div>
                        <span className="font-mono text-xs uppercase tracking-widest opacity-60 mr-2">Your answer:</span>
                        <span style={{ color: isCorrect ? 'var(--success)' : 'var(--error)' }}>
                          {userAns !== undefined && userAns !== '' ? String(userAns) : <em className="opacity-60">No answer</em>}
                        </span>
                      </div>
                      {!isCorrect && (
                        <div>
                          <span className="font-mono text-xs uppercase tracking-widest opacity-60 mr-2">Correct answer:</span>
                          <span style={{ color: 'var(--success)' }}>{String(q.correct_answer)}</span>
                        </div>
                      )}
                      {(evalData.feedback || q.explanation) && (
                        <div className="mt-2 pt-2 border-t" style={{ borderColor: 'var(--border)' }}>
                          <div className="font-mono text-xs uppercase tracking-widest opacity-60 mb-1">Explanation</div>
                          <div className="whitespace-pre-wrap opacity-90">
                            {evalData.feedback && !evalData.feedback.includes("couldn't review")
                              ? evalData.feedback
                              : (q.explanation || 'No explanation available.')}
                          </div>
                          {(evalData.formula_used || q.formula) && (
                            <div className="mt-2 p-2 rounded font-mono text-xs" style={{ backgroundColor: 'var(--surface-secondary)' }}>
                              📐 {evalData.formula_used || q.formula}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </motion.div>

          {/* Actions */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="flex flex-col sm:flex-row gap-3 justify-center pt-2"
          >
            <button
              onClick={onBack}
              className="btn-primary flex items-center justify-center gap-2 px-6 py-3"
              data-testid="complete-back-btn"
            >
              <BookOpen size={18} weight="fill" />
              {recommendedCh ? `Back to Dashboard · Ch.${recommendedCh} next` : 'Back to Dashboard'}
            </button>
            <button
              onClick={() => {
                setCompleted(false);
                setResults(null);
                setCurrentQuestion(0);
                setAnswers({});
                setEvaluations({});
                generateQuiz();
              }}
              className="px-6 py-3 border rounded font-semibold transition-all hover:bg-[#F0EBE1] flex items-center justify-center gap-2"
              style={{ borderColor: 'var(--border)' }}
              data-testid="complete-retry-btn"
            >
              <CaretRight size={18} weight="bold" />
              Try a New Quiz
            </button>
          </motion.div>
        </div>
      </div>
    );
  }

  const currentQ = quiz?.questions?.[currentQuestion];
  const isCurrentAnswered = evaluations[currentQuestion] !== undefined;
  const allAnswered = Object.keys(evaluations).length === quiz?.questions?.length;

  return (
    <div className="min-h-screen p-6 lg:p-8" style={{ backgroundColor: 'var(--background)' }}>
      {/* Header */}
      <header className="max-w-7xl mx-auto mb-6">
        <div className="flex items-center justify-between">
          <button 
            data-testid="back-to-dashboard-btn"
            onClick={onBack}
            className="flex items-center gap-2 text-sm opacity-60 hover:opacity-100 transition-opacity"
          >
            ← Back to Dashboard
          </button>
          <div className="flex items-center gap-4">
            {quiz?.from_bank && (
              <span className="font-mono text-xs uppercase tracking-widest px-3 py-1 rounded bg-[#2D5A27]/10 text-[#2D5A27]" data-testid="instant-badge">
                ⚡ Instant
              </span>
            )}
            <div className="flex items-center gap-2">
              <Scales size={18} />
              <span className="font-mono text-sm">{quizConfig.standard}</span>
            </div>
            <span className="font-mono text-xs uppercase tracking-widest px-3 py-1 rounded" style={{ backgroundColor: 'var(--surface-secondary)' }}>
              {quizConfig.difficulty}
            </span>
          </div>
        </div>
      </header>

      {/* Master's Intro */}
      {quiz?.masters_intro && currentQuestion === 0 && !evaluations[0] && (
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-7xl mx-auto mb-6"
        >
          <div className="masters-panel">
            <p className="font-display italic pl-6 text-lg" style={{ color: 'var(--text-secondary)' }}>
              {quiz.masters_intro}
            </p>
          </div>
        </motion.div>
      )}

      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Financial Statement */}
        <div className="lg:sticky lg:top-6 lg:self-start">
          <FinancialStatement 
            data={quiz?.statement_data}
            companyName={quiz?.company_name}
            fiscalPeriod={quiz?.fiscal_period}
            statementType={quizConfig.statement_type}
          />
        </div>

        {/* Questions */}
        <div className="space-y-4">
          {/* Progress */}
          <div className="flex items-center gap-2 mb-4">
            {quiz?.questions?.map((_, idx) => (
              <div 
                key={idx}
                className={`h-2 flex-1 rounded ${
                  evaluations[idx] 
                    ? evaluations[idx].is_correct 
                      ? 'bg-[#2D5A27]' 
                      : 'bg-[#8B0000]'
                    : idx === currentQuestion 
                      ? 'bg-[#163300]' 
                      : 'bg-[#E6E1D6]'
                }`}
              />
            ))}
          </div>

          {currentQ && (
            <QuestionPanel
              question={currentQ}
              index={currentQuestion}
              total={quiz.questions.length}
              onAnswer={handleAnswer}
              userAnswer={answers[currentQuestion]}
              evaluation={evaluations[currentQuestion]}
              isSubmitting={isSubmitting}
            />
          )}

          {/* Navigation */}
          <div className="flex justify-between pt-4">
            <button
              data-testid="prev-question-btn"
              onClick={() => setCurrentQuestion(prev => Math.max(0, prev - 1))}
              disabled={currentQuestion === 0}
              className="px-4 py-2 border rounded disabled:opacity-30"
              style={{ borderColor: 'var(--border)' }}
            >
              ← Previous
            </button>
            
            {isCurrentAnswered && (
              currentQuestion < quiz.questions.length - 1 ? (
                <button
                  data-testid="next-question-btn"
                  onClick={handleNext}
                  className="btn-primary"
                >
                  Next Question →
                </button>
              ) : allAnswered ? (
                <button
                  data-testid="complete-quiz-btn"
                  onClick={handleComplete}
                  className="btn-primary flex items-center gap-2"
                >
                  <Trophy size={18} weight="fill" />
                  Complete Quiz
                </button>
              ) : (
                <span className="text-sm opacity-60 self-center">Answer all questions to complete</span>
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Main App Component
function App() {
  const [view, setView] = useState('loading'); // 'loading', 'auth', 'dashboard', 'quiz'
  const [user, setUser] = useState(null);
  const [quizConfig, setQuizConfig] = useState(null);
  const [progress, setProgress] = useState(null);
  const [hikariStatus, setHikariStatus] = useState(null);
  const [learningProfile, setLearningProfile] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [tutorOpen, setTutorOpen] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  // Check if user is already logged in
  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      setView('dashboard');
      loadAllData();
    } catch (err) {
      // Not logged in
      setView('auth');
    }
  };

  const loadAllData = () => {
    loadProgress();
    loadHikariStatus();
    loadLearningProfile();
    loadSubscription();
  };

  const loadProgress = async () => {
    try {
      const response = await axios.get(`${API}/progress`);
      setProgress(response.data);
    } catch (err) {
      console.error('Failed to load progress:', err);
    }
  };

  const loadHikariStatus = async () => {
    try {
      const response = await axios.get(`${API}/hikari/status`);
      setHikariStatus(response.data);
    } catch (err) {
      console.error('Failed to load Hikari status:', err);
    }
  };

  const loadLearningProfile = async () => {
    try {
      const response = await axios.get(`${API}/learning-profile`);
      setLearningProfile(response.data);
    } catch (err) {
      console.error('Failed to load learning profile:', err);
    }
  };

  const loadSubscription = async () => {
    try {
      const response = await axios.get(`${API}/subscription/status`);
      setSubscription(response.data);
    } catch (err) {
      console.error('Failed to load subscription:', err);
    }
  };

  // Handle Stripe return — poll payment status, then refresh sub + user
  usePaymentPolling(() => {
    loadSubscription();
    checkAuth();
    setUpgradeOpen(false);
  });

  const handleAuthSuccess = (userData) => {
    // Persist tokens for Bearer auth (bypasses iframe cookie issues)
    if (userData?.access_token) {
      authStorage.setTokens(userData.access_token, userData.refresh_token);
    }
    setUser(userData);
    setView('dashboard');
    loadAllData();
  };

  const handleLogout = () => {
    authStorage.clear();
    setUser(null);
    setProgress(null);
    setLearningProfile(null);
    setSubscription(null);
    setView('auth');
  };

  const handleStartQuiz = (config) => {
    setQuizConfig(config);
    setView('quiz');
  };

  const handleBackToDashboard = () => {
    setView('dashboard');
    setQuizConfig(null);
    loadProgress();
    loadHikariStatus();
    loadLearningProfile();
    loadSubscription();
  };

  const handleOpenUpgrade = () => setUpgradeOpen(true);
  const handleOpenTutor = () => setTutorOpen(true);

  // Loading state
  if (view === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--background)' }}>
        <div className="text-center">
          <GraduationCap size={64} weight="fill" style={{ color: 'var(--primary)' }} className="mx-auto mb-4" />
          <div className="font-mono text-sm opacity-60">Loading...</div>
        </div>
      </div>
    );
  }

  // Auth screen
  if (view === 'auth') {
    return (
      <AuthScreen onAuthSuccess={handleAuthSuccess} />
    );
  }

  return (
    <div className="App">
      <AnimatePresence mode="wait">
        {view === 'dashboard' ? (
          <motion.div
            key="dashboard"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <Dashboard 
              onStartQuiz={handleStartQuiz} 
              progress={progress} 
              hikariStatus={hikariStatus} 
              learningProfile={learningProfile}
              user={user}
              onLogout={handleLogout}
              subscription={subscription}
              onOpenUpgrade={handleOpenUpgrade}
              onOpenTutor={handleOpenTutor}
            />
          </motion.div>
        ) : (
          <motion.div
            key="quiz"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <QuizScreen quizConfig={quizConfig} onBack={handleBackToDashboard} />
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Floating Calculator - Available on all pages */}
      <FloatingCalculator />

      {/* AI Tutor launcher + modal */}
      <TutorFab onOpen={handleOpenTutor} />
      <AITutor
        open={tutorOpen}
        onClose={() => setTutorOpen(false)}
        isPro={!!subscription?.is_pro}
        onUpgrade={() => {
          setTutorOpen(false);
          setUpgradeOpen(true);
        }}
      />

      {/* Upgrade / Stripe checkout modal */}
      <UpgradeModal
        open={upgradeOpen}
        onClose={() => setUpgradeOpen(false)}
        subscription={subscription}
      />
    </div>
  );
}

export default App;
